import React, { useState, useEffect } from "react";
import { useGetCategoriesQuery } from "../../templates/ecommerce/product/api/productTaxonomyApi";
import { decodeHtmlEntities } from "../../common/helpers/decodeHtmlEntities";
import Skeleton from "react-loading-skeleton";

/**
 * Renders a desktop mega menu using the unified hierarchical category tree.
 * Displays three levels of depth (L1 Sidebar, L2/L3 Grid) and category showcases.
 */
const MegaMenu: React.FC = () => {
  // We now use the main categories query instead of the specialized mega-menu one.
  const { data: menuItems, isLoading, isError } = useGetCategoriesQuery();

  // Track hovered top-level category ID.
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);

  // Set the first category as active by default once data is loaded.
  useEffect(() => {
    if (menuItems && menuItems.length > 0 && activeCategoryId === null) {
      setActiveCategoryId(menuItems[0].id);
    }
  }, [menuItems, activeCategoryId]);

  if (isLoading) {
    return (
      <div className="mega-menu">
        <div className="mega-menu__loading">
          <Skeleton height={400} />
        </div>
      </div>
    );
  }

  // If there's an error or the tree is empty, render nothing to avoid breaking the header.
  if (isError || !menuItems || menuItems.length === 0) return null;

  // Find the active top-level category object (L1) based on hover state.
  const activeCategory =
    menuItems.find((item) => item.id === activeCategoryId) || menuItems[0];

  return (
    <div className="mega-menu">
      <div className="mega-menu__container">
        {/* Left Column: Top Level Categories (L1) */}
        <div className="mega-menu__sidebar">
          <ul className="mega-menu__nav">
            {menuItems.map((item) => (
              <li
                key={item.id}
                className={`mega-menu__nav-item ${
                  activeCategoryId === item.id ? "is-active" : ""
                }`}
                onMouseEnter={() => setActiveCategoryId(item.id)}
              >
                <a href={item.link} className="mega-menu__nav-link">
                  {decodeHtmlEntities(item.name)}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Middle Column: Subcategories (L2 & L3) */}
        <div className="mega-menu__subcategories">
          {activeCategory.children && activeCategory.children.length > 0 ? (
            <div className="mega-menu__sub-grid">
              {activeCategory.children.map((l2) => (
                <div key={l2.id} className="mega-menu__sub-group">
                  {/* Level 2 Title */}
                  <a href={l2.link} className="mega-menu__sub-title">
                    {decodeHtmlEntities(l2.name)}
                  </a>

                  {/* Level 3 List: Children of L2 */}
                  {l2.children && l2.children.length > 0 && (
                    <ul className="mega-menu__grandchild-list">
                      {l2.children.map((l3) => (
                        <li key={l3.id}>
                          <a
                            href={l3.link}
                            className="mega-menu__grandchild-link"
                          >
                            {decodeHtmlEntities(l3.name)}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          ) : (
            // Empty state for categories without sub-items
            <div className="mega-menu__empty-sub"></div>
          )}
        </div>

        {/* Right Column: Showcase Items (Gallery) */}
        <div className="mega-menu__showcase">
          {activeCategory.showcase && activeCategory.showcase.length > 0 && (
            <div className="mega-menu__showcase-grid">
              {activeCategory.showcase.map((item, index) => (
                <a
                  key={index}
                  href={item.link}
                  className="mega-menu__showcase-item"
                >
                  <img
                    src={item.image.url}
                    alt={item.title || item.image.alt}
                    className="mega-menu__showcase-image"
                  />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MegaMenu;
